import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { BlogPostForm } from '@/components/blog/BlogPostForm';
import { useBlogPostForm, BlogPostFormData } from '@/hooks/blog/useBlogPostForm';
import { Database } from '@/integrations/supabase/types';
import { useAuth } from '@/contexts/AuthContext';

type ContentStatus = Database['public']['Enums']['content_status'];

const EditBlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [originalAuthorId, setOriginalAuthorId] = useState<string | null>(null);
  
  const {
    formData,
    setFormData,
    initialFormData,
    setInitialFormData,
    selectedItineraryIds,
    setSelectedItineraryIds,
    initialItineraryIds,
    setInitialItineraryIds,
    selectedPackingListId,
    setSelectedPackingListId,
    initialPackingListId,
    setInitialPackingListId,
    selectedTagIds,
    setSelectedTagIds,
    initialTagIds,
    setInitialTagIds,
    activeTab,
    setActiveTab,
    loading: isSubmitting,
    setLoading: setIsSubmitting,
    handleChange,
    handleSelectChange,
    handleImageUploaded,
    handleDateTimeChange,
    handleContentChange,
    hasFormChanged,
    contentEdited,
    setContentEdited
  } = useBlogPostForm();

  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blogPost', id],
    queryFn: async () => {
      console.log("Fetching blog post with ID:", id);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Blog post not found');
      
      console.log("Fetched blog post data:", data);
      if (data.author_id) {
        setOriginalAuthorId(data.author_id);
        console.log("Original author ID stored:", data.author_id);
      }
      return data;
    },
  });

  const { data: linkedItineraries, isLoading: isLoadingItineraries } = useQuery({
    queryKey: ['linkedItineraries', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_itinerary_links')
        .select('itinerary_id')
        .eq('blog_post_id', id);

      if (error) throw error;
      return data || [];
    },
    enabled: !!id,
  });

  const { data: linkedPackingList, isLoading: isLoadingPackingList } = useQuery({
    queryKey: ['linkedPackingList', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_packing_list_links')
        .select('packing_list_id')
        .eq('blog_post_id', id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: linkedTags, isLoading: isLoadingTags } = useQuery({
    queryKey: ['linkedTags', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts_tags')
        .select('tag_id')
        .eq('blog_post_id', id);

      if (error) throw error;
      return data || [];
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (post) {
      console.log("Setting form data from post:", post);
      
      const postData = {
        title: post.title || '',
        slug: post.slug || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        featured_image: post.featured_image || '',
        status: post.status || 'draft',
        seo_title: post.seo_title || '',
        seo_description: post.seo_description || '',
        seo_keywords: post.seo_keywords || '',
        scheduled_at: post.scheduled_at || null,
      };
      
      setFormData(postData);
      setInitialFormData(postData);
      
      setContentEdited(false);
      console.log("Reset contentEdited flag to false after loading post");
    }
  }, [post, setFormData, setInitialFormData, setContentEdited]);

  useEffect(() => {
    if (linkedItineraries) {
      const itineraryIds = linkedItineraries.map(link => link.itinerary_id);
      setSelectedItineraryIds(itineraryIds);
      setInitialItineraryIds(itineraryIds);
    }
  }, [linkedItineraries, setSelectedItineraryIds, setInitialItineraryIds]);

  useEffect(() => {
    if (linkedPackingList) {
      setSelectedPackingListId(linkedPackingList.packing_list_id);
      setInitialPackingListId(linkedPackingList.packing_list_id);
    }
  }, [linkedPackingList, setSelectedPackingListId, setInitialPackingListId]);

  useEffect(() => {
    if (linkedTags) {
      const tagIds = linkedTags.map(link => link.tag_id);
      setSelectedTagIds(tagIds);
      setInitialTagIds(tagIds);
    }
  }, [linkedTags, setSelectedTagIds, setInitialTagIds]);

  const updateBlogPost = useMutation({
    mutationFn: async () => {
      console.log("Starting update for blog post ID:", id);
      console.log("Current form data:", formData);
      console.log("Initial form data:", initialFormData);
      console.log("Content edited flag:", contentEdited);
      console.log("Original author ID being preserved:", originalAuthorId);
      
      console.log("Content comparison:");
      console.log("Initial content length:", initialFormData?.content?.length);
      console.log("New content length:", formData.content?.length);
      console.log("Content edited flag state:", contentEdited);
      console.log("Content to be saved (sample):", formData.content?.substring(0, 100) + "...");
      
      const updateData = {
        title: formData.title,
        slug: formData.slug,
        excerpt: formData.excerpt,
        content: formData.content,
        status: formData.status,
        featured_image: formData.featured_image || null,
        seo_title: formData.seo_title,
        seo_description: formData.seo_description,
        seo_keywords: formData.seo_keywords,
        scheduled_at: formData.scheduled_at,
        updated_at: new Date().toISOString(),
        author_id: originalAuthorId || post?.author_id
      };
      
      console.log("Final update data being sent:", {
        ...updateData,
        content: updateData.content?.substring(0, 100) + "..."
      });
      console.log("Content to be saved length:", updateData.content?.length);
      console.log("Original author ID being preserved:", updateData.author_id);
      console.log("Current user ID (not being used for author_id):", user?.id);
      
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .update(updateData)
          .eq('id', id)
          .select();

        if (error) {
          console.error("Error updating blog post:", error);
          throw error;
        }

        console.log("Blog post updated successfully:", data);
        
        const { error: deleteError } = await supabase
          .from('blog_itinerary_links')
          .delete()
          .eq('blog_post_id', id);

        if (deleteError) throw deleteError;

        if (selectedItineraryIds.length > 0) {
          const itineraryLinks = selectedItineraryIds.map(itineraryId => ({
            blog_post_id: id!,
            itinerary_id: itineraryId
          }));
          
          const { error: linkError } = await supabase
            .from('blog_itinerary_links')
            .insert(itineraryLinks);
            
          if (linkError) throw linkError;
        }

        if (selectedPackingListId) {
          await supabase
            .from('blog_packing_list_links')
            .delete()
            .eq('blog_post_id', id);
            
          const { error: packingListLinkError } = await supabase
            .from('blog_packing_list_links')
            .insert({
              blog_post_id: id!,
              packing_list_id: selectedPackingListId
            });
            
          if (packingListLinkError) throw packingListLinkError;
        }

        const { error: deleteTagsError } = await supabase
          .from('blog_posts_tags')
          .delete()
          .eq('blog_post_id', id);

        if (deleteTagsError) throw deleteTagsError;

        if (selectedTagIds.length > 0) {
          const tagLinks = selectedTagIds.map(tagId => ({
            blog_post_id: id!,
            tag_id: tagId
          }));
          
          const { error: tagLinkError } = await supabase
            .from('blog_posts_tags')
            .insert(tagLinks);
            
          if (tagLinkError) throw tagLinkError;
        }

        return data;
      } catch (error) {
        console.error("Caught error during update:", error);
        throw error;
      }
    },
    onSuccess: () => {
      if (formData.status === 'scheduled') {
        toast.success(`Blog post scheduled for publication on ${new Date(formData.scheduled_at!).toLocaleString()}`);
      } else {
        toast.success('Blog post updated successfully');
      }
      
      queryClient.invalidateQueries({ queryKey: ['blogPost'] });
      queryClient.invalidateQueries({ queryKey: ['linkedItineraries'] });
      queryClient.invalidateQueries({ queryKey: ['linkedPackingList'] });
      queryClient.invalidateQueries({ queryKey: ['linkedTags'] });
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
      
      setContentEdited(false);
      console.log("Reset contentEdited flag after successful update");
      
      setTimeout(() => {
        navigate(`/dashboard/blogs/${id}`);
      }, 500);
    },
    onError: (error) => {
      console.error('Error updating blog post:', error);
      toast.error(`Failed to update: ${error.message}`);
      setIsSubmitting(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }
    
    if (!formData.slug) {
      toast.error('Slug is required');
      return;
    }
    
    console.log("Form data before submit:", {
      title: formData.title,
      content_length: formData.content?.length,
      initial_content_length: initialFormData?.content?.length,
      content_edited_flag: contentEdited,
      has_form_changed: hasFormChanged()
    });
    
    if (contentEdited || hasFormChanged()) {
      console.log("Content edited or form has changed, proceeding with update");
      console.log("Content edited flag:", contentEdited);
      console.log("Form changed per field comparison:", hasFormChanged());
      
      setIsSubmitting(true);
      updateBlogPost.mutate();
    } else {
      console.log("No changes detected, skipping update");
      toast.info('No changes detected');
      navigate(`/dashboard/blogs/${id}`);
    }
  };

  if (isLoading || isLoadingItineraries || isLoadingPackingList || isLoadingTags) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="glass-card">
        <CardContent className="flex flex-col items-center justify-center h-64">
          <p className="text-destructive text-lg mb-4">
            {error instanceof Error ? error.message : 'An error occurred'}
          </p>
          <Button onClick={() => navigate('/dashboard/blogs')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Blogs
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <Button variant="outline" onClick={() => navigate(`/dashboard/blogs/${id}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <h1 className="text-3xl font-bold tracking-tight ml-4">Edit Blog Post</h1>
      </div>

      <BlogPostForm
        formData={formData}
        selectedItineraryIds={selectedItineraryIds}
        setSelectedItineraryIds={setSelectedItineraryIds}
        selectedPackingListId={selectedPackingListId}
        setSelectedPackingListId={setSelectedPackingListId}
        selectedTagIds={selectedTagIds}
        setSelectedTagIds={setSelectedTagIds}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        loading={isSubmitting}
        handleChange={handleChange}
        handleSelectChange={handleSelectChange}
        handleImageUploaded={handleImageUploaded}
        handleDateTimeChange={handleDateTimeChange}
        handleContentChange={handleContentChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(`/dashboard/blogs/${id}`)}
        submitLabel="Save Changes"
        blogPostId={id!}
      />
    </div>
  );
}

export default EditBlogPost;
